import { redirect } from "@tanstack/react-router";
import * as Sentry from "@sentry/tanstackstart-react";
import { createServerFn } from "@tanstack/react-start";
import { authMiddleware } from "#/modules/util.auth/middleware.auth";
import { Result } from "better-result";
import { z } from "zod/v4";
import {
  getValidatedImportDataFromCache,
  importStudentsToBackend,
  removeImportFromCache,
  storeImportDataInCache,
  validateImportFile,
  type ParseFileError,
} from "#/modules/school.import/api.import-file.server";
import { translateApiError } from "#/modules/util.errors/translate";
import { m } from "#/paraglide/messages";
import {
  getImportPreferences,
  persistImportPreferences,
  type ImportContextParams,
} from "#/modules/school.import/api.import-preferences.server";
import { getSchoolMetaData } from "#/modules/school/api.school.server";
import { stringOrEmptySchema } from "#/modules/util.helpers/zod-primitives";

const importContextSchema = z.object({
  school: z.string(),
  type: z.string(),
  locale: z.string(),
});

export const getImportIndexData = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator(importContextSchema)
  .handler(async ({ data }) => {
    const metadataResult = await getSchoolMetaData();
    if (Result.isError(metadataResult)) throw metadataResult.error;
    // A blocked school stays on the start page (which explains the block) even when an earlier
    // upload is still staged — the review flow could only end in the same refusal.
    if (!metadataResult.value.isAllowedToImport) return {};

    const importData = await getValidatedImportDataFromCache(data);

    if (importData) {
      throw redirect({
        to: "/$locale/{-$schoolyear}/$school/detail/import/$type/review/$status",
        params: {
          locale: data.locale,
          school: data.school,
          type: data.type,
          status: "klassen",
        },
      });
    }

    return {};
  });

export const getImportReviewData = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator(importContextSchema)
  .handler(async ({ data }) => {
    const importData = await getValidatedImportDataFromCache(data);

    if (!importData) {
      throw redirect({
        to: "/$locale/{-$schoolyear}/$school/detail/import/$type",
        params: { locale: data.locale, school: data.school, type: data.type },
      });
    }

    const importPreferences = await getImportPreferences(data);

    return { importData, importPreferences };
  });

export const getImportReviewStatusData = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator(importContextSchema.extend({ status: z.string() }))
  .handler(async ({ data }) => {
    const importData = await getValidatedImportDataFromCache(data);
    const importPreferences = await getImportPreferences(data);

    if (!importData) {
      throw redirect({
        to: "/$locale/{-$schoolyear}/$school/detail/import/$type",
        params: { locale: data.locale, school: data.school, type: data.type },
      });
    }

    if (data.status === "schoolverlaters" && !importPreferences.allowDeletes) {
      throw redirect({
        to: "/$locale/{-$schoolyear}/$school/detail/import/$type/review/$status",
        params: {
          locale: data.locale,
          school: data.school,
          type: data.type,
          status: "klassen",
        },
      });
    }

    return null;
  });

export const getImportSubmitData = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator(importContextSchema)
  .handler(async ({ data }) => {
    const importData = await getValidatedImportDataFromCache(data);
    const importPreferences = await getImportPreferences(data);

    if (!importData) {
      throw redirect({
        to: "/$locale/{-$schoolyear}/$school/detail/import/$type",
        params: { locale: data.locale, school: data.school, type: data.type },
      });
    }

    return { importData, importPreferences };
  });

/**
 * Validates an uploaded import file. The client posts a multipart FormData (file + context)
 * which TanStack Start forwards to the handler. Success on the upload step redirects into the
 * review flow; a re-upload from within `/review` returns a plain result so the caller stays put.
 */
export const submitValidateImportFile = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator((data: FormData) => {
    if (!(data instanceof FormData)) {
      throw new Error("submitValidateImportFile expects FormData");
    }
    return data;
  })
  .handler(async ({ data: formData }) => {
    const getFormString = (key: string) => stringOrEmptySchema.parse(formData.get(key));
    const school = getFormString("school");
    const type = getFormString("type");
    const locale = getFormString("locale");
    const referer = getFormString("referer");
    const file = formData.get("file");

    const params: ImportContextParams = { school, type };

    const parsed = await validateImportFile({ file });
    if (Result.isError(parsed)) {
      // Return the typed error rather than throwing a flattened message: the upload component
      // renders InvalidHeaders with markup (template link), which needs the title client-side.
      const parseError: ParseFileError = {
        status: parsed.error.problem.status,
        title: parsed.error.problem.title,
        detail: parsed.error.problem.detail,
      };
      return { success: false as const, error: parseError };
    }
    if (parsed.value) {
      await storeImportDataInCache(params, parsed.value);
    }

    if (referer.includes("/review")) {
      return { success: true as const };
    }

    throw redirect({
      to: "/$locale/{-$schoolyear}/$school/detail/import/$type/review/$status",
      params: {
        locale,
        school,
        type,
        status: "klassen",
      },
    });
  });

export const submitImportPreferences = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(
    z.object({
      school: z.string(),
      type: z.string(),
      allowDeletes: z.boolean(),
    }),
  )
  .handler(async ({ data }) => {
    await persistImportPreferences(data, { allowDeletes: data.allowDeletes });
    return { success: true as const };
  });

export const submitCancelImport = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(importContextSchema)
  .handler(async ({ data }) => {
    await removeImportFromCache(data);

    throw redirect({
      to: "/$locale/{-$schoolyear}/$school/detail/import/$type",
      params: { locale: data.locale, school: data.school, type: data.type },
    });
  });

export const submitImportStudents = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(importContextSchema)
  .handler(async ({ data }) => {
    const importData = await getValidatedImportDataFromCache(data);
    const importPreferences = await getImportPreferences(data);

    if (!importData) {
      // Staged data can expire (cache TTL) or be consumed by a submit in another tab while the
      // review page stays open; restart the flow like the GET handlers instead of crashing.
      throw redirect({
        to: "/$locale/{-$schoolyear}/$school/detail/import/$type",
        params: { locale: data.locale, school: data.school, type: data.type },
      });
    }

    let result: Awaited<ReturnType<typeof importStudentsToBackend>>;
    try {
      result = await importStudentsToBackend({
        ...importData,
        allowDeletes: importPreferences.allowDeletes,
        createNewSchooljaar: data.type === "start-new-year",
      });
    } catch (cause) {
      // Network-level failure (fetch rejected before any response) keeps the form-error surface.
      Sentry.captureException(cause, { tags: { flow: "school-import" } });
      return {
        success: false as const,
        reason: "NetworkError",
        formErrors: [m["common.something_went_wrong"]()],
      };
    }

    if (Result.isError(result)) {
      // Import flow uses plural "een of meerdere leerlingen" wording.
      return {
        success: false as const,
        reason: result.error.title,
        status: result.error.status,
        formErrors: [
          result.error.status === 422
            ? m["import.api_errors.validation_error"]()
            : result.error.status === 404
              ? m["import.api_errors.student_not_found"]()
              : translateApiError(result.error, {
                  LeerlingAlreadyExists: () => m["import.api_errors.student_already_exists"](),
                }),
        ],
      };
    }

    await removeImportFromCache(data);

    throw redirect({
      to: "/$locale/{-$schoolyear}/$school/students/overview",
      params: { locale: data.locale, school: data.school },
    });
  });

export const getImportLayoutData = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator(z.object({ school: z.string(), type: z.string() }))
  .handler(async ({ data }) => {
    const metadataResult = await getSchoolMetaData();
    if (Result.isError(metadataResult)) throw metadataResult.error;
    const { currentSchooljaar, isAllowedToImport, licentieType } = metadataResult.value;
    const importPreferences = await getImportPreferences(data);

    return { currentSchooljaar, importPreferences, isAllowedToImport, licentieType };
  });
